import React from 'react';
import {
    Briefcase,
    CheckCircle2,
    ClipboardList,
    Compass,
    Lightbulb,
    Microscope,
    Palette,
    Sparkles,
    Users,
    Wrench
} from 'lucide-react';

const PRINT_LOGO_SRC = '/logo512.png';
const PRINT_WATERMARK_SRC = '/logo192.png';
const FULL_PRINT_SURFACE_WIDTH_MM = 186;
const FULL_PRINT_PAGE_MIN_HEIGHT_MM = 273;

const ORIENTATION_ICONS = {
    R: Wrench,
    I: Microscope,
    A: Palette,
    S: Users,
    E: Briefcase,
    C: ClipboardList
};

const bandToneClasses = {
    Strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Developing: 'bg-amber-50 text-amber-700 border-amber-200',
    Emerging: 'bg-slate-50 text-slate-700 border-slate-200'
};

const insightCards = [
    {
        icon: Compass,
        title: 'Career is a path, not only a job',
        text: 'This report helps you think in journeys, not just titles. A career grows through choices, experiences, and direction over time.'
    },
    {
        icon: Lightbulb,
        title: 'Capabilities + interest = opportunity',
        text: 'Part A reveals what naturally engages you. Part B shows how you move through uncertainty, challenge, and decision.'
    },
    {
        icon: Sparkles,
        title: 'The future keeps moving',
        text: 'Future-ready careers often emerge where genuine interest stays connected to visible capability and adaptive behaviour.'
    }
];

const pageBaseStyle = {
    minHeight: `${FULL_PRINT_PAGE_MIN_HEIGHT_MM}mm`,
    boxSizing: 'border-box',
    padding: '5.5mm'
};

const surfaceStyle = {
    width: `${FULL_PRINT_SURFACE_WIDTH_MM}mm`
};

const FixedPrintPageFrame = ({ pageLabel, title, subtitle, children }) => {
    return (
        <section
            className="full-results-dedicated-print-page relative overflow-hidden rounded-[20px] border border-[#ece4dc] bg-white text-[#171717] shadow-[0_14px_32px_rgba(15,23,42,0.05)]"
            style={pageBaseStyle}
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(243,119,65,0.10),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(146,39,143,0.08),_transparent_36%),linear-gradient(180deg,_rgba(255,255,255,0.98)_0%,_rgba(255,252,249,1)_100%)]" />
            <div
                className="pointer-events-none absolute inset-[5mm] opacity-[0.045]"
                style={{
                    backgroundImage: `url(${PRINT_WATERMARK_SRC})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '18mm 18mm',
                    backgroundPosition: '0 0'
                }}
            />

            <div className="relative z-10 flex min-h-full flex-col gap-3">
                <header className="flex items-start justify-between gap-3 border-b border-[#eee7df] pb-2.5">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-[14px] border border-[#f1e8de] bg-white p-1.5 shadow-sm">
                            <img src={PRINT_LOGO_SRC} alt="Yancha" className="h-full w-full object-contain" />
                        </div>

                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--color-brand)]">Yancha Career Report</p>
                            <h1 className="mt-1 text-[18px] leading-tight font-black text-[#171717] brand-font">{title}</h1>
                        </div>
                    </div>

                    <div className="max-w-[160px] text-right">
                        <p className="text-[8px] font-black uppercase tracking-[0.22em] text-[#8a7d74]">{pageLabel}</p>
                        <p className="mt-1 text-[10px] leading-[1.35] font-semibold text-[#4b5563] brand-text">{subtitle}</p>
                    </div>
                </header>

                <div className="relative z-10 min-h-0 flex-1">
                    {children}
                </div>
            </div>
        </section>
    );
};

const PrintSectionTitle = ({ eyebrow, title, intro }) => {
    return (
        <div className="mb-2.5">
            <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[var(--color-brand)]">{eyebrow}</p>
            <h2 className="mt-1 text-[19px] leading-tight font-black text-[#171717] brand-font">{title}</h2>
            <p className="mt-1.5 text-[10px] leading-[1.5] text-[#57534e] brand-text">{intro}</p>
        </div>
    );
};

const PrintSnapshotCard = ({ label, value, helper, icon: Icon, accentColor }) => {
    return (
        <div className="rounded-[16px] border border-white/12 bg-white/10 px-2.5 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            <div className="flex items-start gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] bg-white/10">
                    <Icon size={16} color={accentColor || '#ffffff'} strokeWidth={2.3} />
                </div>

                <div>
                    <p className="text-[8px] font-black uppercase tracking-[0.18em] text-white/65">{label}</p>
                    <p className="mt-0.5 text-[13px] leading-tight font-black text-white brand-font">{value}</p>
                    <p className="mt-0.5 text-[10px] leading-[1.35] text-white/72 brand-text">{helper}</p>
                </div>
            </div>
        </div>
    );
};

const FixedRiasecRadar = ({ entries, dominantOrientation }) => {
    const size = 214;
    const center = size / 2;
    const maxRadius = 69;
    const labelRadius = maxRadius + 24;
    const gridLevels = [0.25, 0.5, 0.75, 1];

    const pointAt = (radius, index) => {
        const angle = ((-90 + index * 60) * Math.PI) / 180;

        return {
            x: center + Math.cos(angle) * radius,
            y: center + Math.sin(angle) * radius
        };
    };

    const gridPolygons = gridLevels.map((level) => (
        entries
            .map((_, index) => {
                const point = pointAt(maxRadius * level, index);
                return `${point.x},${point.y}`;
            })
            .join(' ')
    ));

    const dataPolygon = entries
        .map((entry, index) => {
            const point = pointAt((entry.score / 10) * maxRadius, index);
            return `${point.x},${point.y}`;
        })
        .join(' ');

    return (
        <div className="full-results-dedicated-print-card relative flex aspect-square w-full items-center justify-center rounded-[18px] border border-[#efe6dd] bg-[linear-gradient(180deg,_#ffffff_0%,_#fbf8fd_100%)] p-3 shadow-sm">
            <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full">
                {gridPolygons.map((points, index) => (
                    <polygon
                        key={`grid-${index}`}
                        points={points}
                        fill="none"
                        stroke="#eadff0"
                        strokeWidth="1.1"
                    />
                ))}

                {entries.map((entry, index) => {
                    const point = pointAt(maxRadius, index);

                    return (
                        <line
                            key={`axis-${entry.code}`}
                            x1={center}
                            y1={center}
                            x2={point.x}
                            y2={point.y}
                            stroke="#eadff0"
                            strokeWidth="1"
                        />
                    );
                })}

                <polygon points={dataPolygon} fill="rgba(146,39,143,0.16)" stroke="#92278F" strokeWidth="2.4" />

                {entries.map((entry, index) => {
                    const point = pointAt((entry.score / 10) * maxRadius, index);

                    return (
                        <circle
                            key={`dot-${entry.code}`}
                            cx={point.x}
                            cy={point.y}
                            r="4.8"
                            fill={entry.color}
                            stroke="#ffffff"
                            strokeWidth="2.2"
                        />
                    );
                })}
            </svg>

            {entries.map((entry, index) => {
                const point = pointAt(labelRadius, index);

                return (
                    <div
                        key={`label-${entry.code}`}
                        className="absolute w-[50px] rounded-[12px] border border-white bg-white/96 px-1 py-1 text-center shadow-sm"
                        style={{
                            left: `${(point.x / size) * 100}%`,
                            top: `${(point.y / size) * 100}%`,
                            transform: 'translate(-50%, -50%)'
                        }}
                    >
                        <p className="text-[8px] font-black uppercase tracking-[0.14em]" style={{ color: entry.color }}>
                            {entry.code}
                        </p>
                        <p className="mt-0.5 text-[8px] leading-[1.15] font-bold text-[#171717] brand-text">{entry.title}</p>
                        <p className="mt-0.5 text-[7px] font-semibold text-gray-500">{entry.percent}%</p>
                    </div>
                );
            })}

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-8">
                <div className="rounded-[16px] border border-white bg-white/96 px-3 py-2.5 text-center shadow-md">
                    <p className="text-[8px] font-black uppercase tracking-[0.22em] text-[#8a7d74]">Strongest orientation</p>
                    <p className="mt-1 text-[13px] leading-tight font-black text-[#171717] brand-font">{dominantOrientation.title}</p>
                    <p className="mt-1 text-[9px] leading-[1.3] text-gray-500 brand-text">Holland / RIASEC fit lens</p>
                </div>
            </div>
        </div>
    );
};

const PrintOrientationCard = ({ entry }) => {
    const Icon = ORIENTATION_ICONS[entry.code];

    return (
        <div className="full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
            <div className="flex items-start justify-between gap-2.5">
                <div className="flex items-start gap-2.5">
                    <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border bg-white"
                        style={{ borderColor: `${entry.color}45` }}
                    >
                        <Icon size={16} color={entry.color} strokeWidth={2.3} />
                    </div>

                    <div>
                        <div className="flex items-center gap-2">
                            <p className="text-[13px] leading-tight font-black text-[#171717] brand-font">{entry.title}</p>
                            <span className="rounded-full bg-[#f7f1fb] px-2 py-0.5 text-[7px] font-black uppercase tracking-[0.16em] text-[#92278F]">
                                #{entry.rank}
                            </span>
                        </div>
                        <p className="mt-0.5 text-[9px] font-semibold text-gray-500 brand-text">{entry.descriptor}</p>
                    </div>
                </div>

                <div className="text-right">
                    <p className="text-[15px] leading-none font-black" style={{ color: entry.color }}>{entry.percent}%</p>
                    <p className="mt-0.5 text-[7px] font-semibold uppercase tracking-[0.16em] text-gray-400">fit</p>
                </div>
            </div>

            <p className="mt-2 text-[10px] leading-[1.35] text-[#57534e] brand-text">{entry.summary}</p>

            <div className="mt-2.5 flex flex-wrap gap-1">
                {entry.strengths.map((strength) => (
                    <span
                        key={`${entry.code}-${strength}`}
                        className="rounded-full border px-2 py-1 text-[8px] font-bold uppercase tracking-[0.12em]"
                        style={{
                            borderColor: `${entry.color}30`,
                            color: entry.color,
                            backgroundColor: `${entry.color}12`
                        }}
                    >
                        {strength}
                    </span>
                ))}
            </div>
        </div>
    );
};

const PrintRapdCard = ({ entry }) => {
    const toneClasses = bandToneClasses[entry.bandLabel] || bandToneClasses.Emerging;

    return (
        <div className="full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
            <div className="flex items-start justify-between gap-2.5">
                <div className="flex items-start gap-2.5">
                    <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] text-[12px] font-black text-white"
                        style={{ backgroundColor: entry.color }}
                    >
                        {entry.code}
                    </div>

                    <div>
                        <p className="text-[12px] leading-tight font-black text-[#171717] brand-font">{entry.title}</p>
                        <p className="mt-0.5 text-[9px] font-semibold text-gray-500 brand-text">{entry.descriptor}</p>
                    </div>
                </div>

                <p className="text-[14px] leading-none font-black" style={{ color: entry.color }}>{entry.percent}%</p>
            </div>

            <div className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] brand-text ${toneClasses}`}>
                {entry.bandLabel}
            </div>

            <p className="mt-2 text-[9px] leading-[1.35] text-[#57534e] brand-text">{entry.summary}</p>
            <p className="mt-1.5 text-[9px] leading-[1.35] text-[#57534e] brand-text">{entry.bandText}</p>
        </div>
    );
};

const PrintCareerFocusCard = ({ label, title, subtitle, description, detail, badge, badgeClassName, className = '' }) => {
    return (
        <div className={`full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm ${className}`}>
            <div className="flex items-start justify-between gap-2.5">
                <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">{label}</p>
                    <h3 className="mt-1 text-[17px] leading-tight font-black text-[#171717] brand-font">{title}</h3>
                    {subtitle ? <p className="mt-0.5 text-[10px] font-semibold text-[var(--color-brand)] brand-text">{subtitle}</p> : null}
                </div>

                {badge ? (
                    <span className={badgeClassName}>
                        {badge}
                    </span>
                ) : null}
            </div>

            <p className="mt-2 text-[10px] leading-[1.45] text-[#57534e] brand-text">{description}</p>

            {detail ? (
                <p className="mt-1.5 text-[9px] leading-[1.35] text-[#78716c] brand-text">{detail}</p>
            ) : null}
        </div>
    );
};

const PrintLandscapeColumn = ({ group }) => {
    return (
        <div className="full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
            <div>
                <p className="text-[13px] leading-tight font-black text-[#171717] brand-font">{group.title}</p>
                <p className="mt-0.5 text-[9px] leading-[1.35] text-[#6b7280] brand-text">{group.description}</p>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-1">
                {group.items.map((item) => {
                    const baseClasses = 'rounded-full border px-2 py-1 text-[8px] font-bold leading-[1.2]';

                    if (item.primary) {
                        return (
                            <span
                                key={`${group.key}-${item.title}`}
                                className={`${baseClasses} border-transparent bg-gradient-to-r from-[#92278F] to-[#F37741] text-white shadow-sm`}
                            >
                                {item.title}
                            </span>
                        );
                    }

                    if (item.highlighted) {
                        return (
                            <span
                                key={`${group.key}-${item.title}`}
                                className={`${baseClasses} border-[#e0c6df] bg-[#fdf8ff] text-[#7a246f]`}
                            >
                                {item.title}
                            </span>
                        );
                    }

                    return (
                        <span
                            key={`${group.key}-${item.title}`}
                            className={`${baseClasses} border-[#eee7df] bg-[#fffdfb] text-[#6f625a]`}
                        >
                            {item.title}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const PrintNextStepCard = ({ step, index }) => {
    return (
        <div className="full-results-dedicated-print-card rounded-[16px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
            <div className="flex items-start gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#fff2eb] to-[#f8f0ff] text-[#92278F]">
                    <CheckCircle2 size={16} strokeWidth={2.3} />
                </div>

                <div>
                    <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Step {index + 1}</p>
                    <p className="mt-0.5 text-[13px] leading-tight font-black text-[#171717] brand-font">{step.title}</p>
                    <p className="mt-1.5 text-[10px] leading-[1.35] text-[#57534e] brand-text">{step.text}</p>
                </div>
            </div>
        </div>
    );
};

const FullResultsPrintPage = ({ report, participantHeading, participantName }) => {
    const dominantIcon = ORIENTATION_ICONS[report.dominantOrientation.code] || Compass;
    const participantLabel = participantName === 'Your' ? 'Prepared as a personalized full report' : `Prepared for ${participantName}`;

    return (
        <div className="full-results-dedicated-print-portal" aria-hidden="true">
            <div className="full-results-dedicated-print-surface mx-auto flex flex-col gap-3 bg-white text-[#171717]" style={surfaceStyle}>
                <FixedPrintPageFrame pageLabel="Page 1 of 4" title="Full Career Report" subtitle={participantLabel}>
                    <div className="flex flex-col gap-3">
                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#eee7df] bg-[radial-gradient(circle_at_top_left,_rgba(243,119,65,0.16),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(146,39,143,0.13),_transparent_36%),linear-gradient(180deg,_#ffffff_0%,_#fffdfb_100%)] px-4 py-3 shadow-sm">
                            <div className="grid items-start gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                                <div>
                                    <span className="inline-flex rounded-full border border-[var(--color-brand)]/10 bg-white px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-[var(--color-brand)]">
                                        Full report overview
                                    </span>

                                    <h2 className="mt-2.5 max-w-none text-[27px] leading-[1.02] font-black text-[#171717] brand-font">
                                        <span className="block">{participantHeading}</span>
                                        <span className="block">full career report</span>
                                    </h2>

                                    <p className="mt-2.5 max-w-none text-[12px] leading-[1.45] font-semibold text-[var(--color-subtext)] brand-text">
                                        Career is not just a job to be picked once. It is a path built through interests, capabilities, behaviour, and the opportunities that open in a changing future.
                                    </p>

                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        <span className="rounded-full border border-[#eadff0] bg-white px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-[#92278F]">
                                            Interest code: {report.interestCode}
                                        </span>
                                        <span className="rounded-full border border-[#f2dccf] bg-white px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-[#F37741]">
                                            Behavioural base: {report.behaviorStrengthLabel}
                                        </span>
                                        <span className="rounded-full border border-[#eadff0] bg-white px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-[#171717]">
                                            Spotlight career: {report.careers.primary.title}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="full-results-dedicated-print-grid grid gap-2.5" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                            {insightCards.map((card) => {
                                const Icon = card.icon;

                                return (
                                    <div key={card.title} className="full-results-dedicated-print-card rounded-[16px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#fff2eb] to-[#f8f0ff] text-[#92278F]">
                                            <Icon size={16} strokeWidth={2.2} />
                                        </div>
                                        <p className="mt-2.5 text-[12px] leading-tight font-black text-[#171717] brand-font">{card.title}</p>
                                        <p className="mt-1.5 text-[9px] leading-[1.4] text-[#57534e] brand-text">{card.text}</p>
                                    </div>
                                );
                            })}
                        </div>

                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                            <PrintSectionTitle
                                eyebrow="Part A | Holland / RIASEC"
                                title="Personality-interest-vocation fit"
                                intro="Part A shows how your interests distribute across six orientations so you can see which activities, environments, and roles are most likely to feel naturally engaging."
                            />

                            <div className="full-results-dedicated-print-grid grid gap-3" style={{ gridTemplateColumns: '72mm minmax(0, 1fr)' }}>
                                <FixedRiasecRadar entries={report.riasecEntries} dominantOrientation={report.dominantOrientation} />

                                <div className="flex flex-col gap-2.5">
                                    <div className="full-results-dedicated-print-card rounded-[18px] border border-[#eadff0] bg-[linear-gradient(180deg,_#fcf8ff_0%,_#ffffff_100%)] px-3 py-3 shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="rounded-full bg-[#f6edfb] px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-[#92278F]">
                                                Strongest orientation
                                            </span>
                                            <span className="rounded-full bg-[#fff3ea] px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-[#F37741]">
                                                Top 3 code: {report.interestCode}
                                            </span>
                                        </div>

                                        <div className="mt-2.5 flex items-start gap-2.5">
                                            <div
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] border bg-white"
                                                style={{ borderColor: `${report.dominantOrientation.color}45` }}
                                            >
                                                {React.createElement(dominantIcon, {
                                                    size: 18,
                                                    color: report.dominantOrientation.color,
                                                    strokeWidth: 2.3
                                                })}
                                            </div>

                                            <div>
                                                <h3 className="text-[18px] leading-tight font-black text-[#171717] brand-font">{report.dominantOrientation.title}</h3>
                                                <p className="mt-0.5 text-[10px] font-semibold text-gray-500 brand-text">{report.dominantOrientation.descriptor}</p>
                                                <p className="mt-2 text-[10px] leading-[1.45] text-[#57534e] brand-text">{report.partA.summary}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="full-results-dedicated-print-grid grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                        <div className="full-results-dedicated-print-card rounded-[16px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
                                            <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Work environment fit</p>
                                            <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">{report.partA.environmentSummary}</p>
                                        </div>

                                        <div className="full-results-dedicated-print-card rounded-[16px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
                                            <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Motivation pattern</p>
                                            <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">{report.partA.motivationSummary}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </FixedPrintPageFrame>

                <FixedPrintPageFrame pageLabel="Page 2 of 4" title="Part A Detailed Orientation Map" subtitle={`Interest code ${report.interestCode}`}>
                    <div className="flex flex-col gap-3">
                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                            <PrintSectionTitle
                                eyebrow="Part A detail"
                                title="Your six orientation signals"
                                intro="Each orientation highlights a different kind of energy. The strongest combinations often show which activities you return to naturally and which environments can sustain your attention over time."
                            />

                            <div className="full-results-dedicated-print-grid grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                {report.riasecEntries.map((entry) => (
                                    <PrintOrientationCard key={entry.code} entry={entry} />
                                ))}
                            </div>
                        </section>

                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-[linear-gradient(180deg,_#ffffff_0%,_#fcf8ff_100%)] px-3.5 py-3 shadow-sm">
                            <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                {[report.dominantOrientation, report.secondaryOrientation, report.tertiaryOrientation].map((entry, index) => (
                                    <div key={entry.code} className="rounded-[16px] border border-[#ece5ee] bg-white px-3 py-2.5 shadow-sm">
                                        <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Top {index + 1}</p>
                                        <p className="mt-0.5 text-[14px] leading-tight font-black text-[#171717] brand-font">{entry.title}</p>
                                        <p className="mt-0.5 text-[10px] font-semibold brand-text" style={{ color: entry.color }}>{entry.percent}% fit signal</p>
                                        <p className="mt-1.5 text-[9px] leading-[1.35] text-[#57534e] brand-text">{entry.summary}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-2.5 rounded-[16px] border border-[#eadff0] bg-white px-3 py-2.5 shadow-sm">
                                <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#92278F]">How Part A guides direction</p>
                                <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">
                                    Your {report.interestCode} interest code anchors the types of environments, activities, and pathways most likely to stay meaningful. Part A becomes more powerful when it is combined with Part B, because interest shows where energy comes from and behaviour shows how that energy can be carried into action.
                                </p>
                            </div>
                        </section>
                    </div>
                </FixedPrintPageFrame>

                <FixedPrintPageFrame pageLabel="Page 3 of 4" title="Part B Behaviour and Career Possibilities" subtitle={report.behaviorStrengthLabel}>
                    <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                        <PrintSectionTitle
                            eyebrow="Part B | RAPD"
                            title="Behavioural platform and career possibilities"
                            intro="Part B interprets your report through the RAPD behavioural framework: Attitude, Perspectives, Resources, and Decision Making. This shows how you are likely to move from interest into visible direction."
                        />

                        <div className="full-results-dedicated-print-grid grid gap-3" style={{ gridTemplateColumns: '4fr 6fr' }}>
                            <div className="flex flex-col gap-2.5">
                                <div className="full-results-dedicated-print-card rounded-[18px] border border-[#f2dccf] bg-[linear-gradient(180deg,_#fff8f3_0%,_#ffffff_100%)] px-3 py-3 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="rounded-full bg-[#fff1e8] px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-[#F37741]">
                                            RAPD explainer
                                        </span>
                                        <span className="rounded-full bg-[#f6edfb] px-2 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-[#92278F]">
                                            {report.behaviorStrengthLabel}
                                        </span>
                                    </div>

                                    <p className="mt-2 text-[10px] leading-[1.45] text-[#57534e] brand-text">{report.partB.summary}</p>
                                    <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">
                                        The future is dynamic, so Part B focuses on how you reset, reflect, use available support, and make decisions when choices are not obvious.
                                    </p>
                                </div>

                                <div className="full-results-dedicated-print-grid grid gap-2.5" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                                    <PrintCareerFocusCard
                                        label="Future Career"
                                        title={report.careers.primary.title}
                                        description={report.careers.primary.why}
                                        detail={report.careers.primary.trajectory}
                                        className="min-h-[116px]"
                                    />
                                </div>
                            </div>

                            <div className="full-results-dedicated-print-grid grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                {report.rapdEntries.map((entry) => (
                                    <PrintRapdCard key={entry.code} entry={entry} />
                                ))}
                            </div>
                        </div>
                    </section>
                </FixedPrintPageFrame>

                <FixedPrintPageFrame pageLabel="Page 4 of 4" title="Final Synthesis and Guidance" subtitle={report.synthesis.archetypeTitle}>
                    <div className="flex flex-col gap-3">
                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                            <PrintSectionTitle
                                eyebrow="Final synthesis"
                                title={report.synthesis.archetypeTitle}
                                intro="This final section brings Part A and Part B together. It is intended as an integrated reading of your interests, behavioural strengths, and visible career possibilities rather than a fixed label."
                            />

                            <div className="full-results-dedicated-print-grid grid gap-3" style={{ gridTemplateColumns: 'minmax(0, 1.04fr) 72mm' }}>
                                <div className="rounded-[20px] bg-gradient-to-br from-[#171717] via-[#23132c] to-[#3d1d43] px-4 py-3.5 text-white shadow-[0_18px_40px_rgba(0,0,0,0.14)]">
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#d6c6e1]">Integrated summary</p>
                                    <h3 className="mt-2 text-[24px] leading-tight font-black brand-font">{report.outcome.label}</h3>
                                    <p className="mt-2 text-[11px] leading-[1.5] text-white/82 brand-text">{report.synthesis.summary}</p>
                                    <p className="mt-1.5 text-[10px] leading-[1.4] text-white/72 brand-text">{report.outcome.description}</p>

                                    <div className="mt-3 rounded-[16px] border border-white/10 bg-white/5 px-3 py-2.5">
                                        <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#cbbad7]">Profile formula</p>
                                        <p className="mt-1 text-[11px] leading-[1.45] text-white/92 brand-text">{report.synthesis.formula}</p>
                                    </div>

                                    <div className="mt-3 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                        <div className="rounded-[14px] border border-white/10 bg-white/5 px-2.5 py-2.5">
                                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#cbbad7]">Energises you</p>
                                            <p className="mt-1 text-[12px] leading-tight font-black brand-font">{report.dominantOrientation.title}</p>
                                            <p className="mt-1 text-[9px] leading-[1.35] text-white/72 brand-text">{report.dominantOrientation.summary}</p>
                                        </div>

                                        <div className="rounded-[14px] border border-white/10 bg-white/5 px-2.5 py-2.5">
                                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#cbbad7]">Carries you forward</p>
                                            <p className="mt-1 text-[12px] leading-tight font-black brand-font">{report.behaviorLead.title}</p>
                                            <p className="mt-1 text-[9px] leading-[1.35] text-white/72 brand-text">{report.behaviorLead.bandText}</p>
                                        </div>

                                        <div className="rounded-[14px] border border-white/10 bg-white/5 px-2.5 py-2.5">
                                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#cbbad7]">Primary career</p>
                                            <p className="mt-1 text-[12px] leading-tight font-black brand-font">{report.careers.primary.title}</p>
                                            <p className="mt-1 text-[9px] leading-[1.35] text-white/72 brand-text">Your current profile most strongly aligns with this visible career direction.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2.5">
                                    {report.synthesis.nextSteps.map((step, index) => (
                                        <PrintNextStepCard key={step.title} step={step} index={index} />
                                    ))}

                                    <div className="full-results-dedicated-print-card rounded-[16px] border border-[#efe6dd] bg-[linear-gradient(180deg,_#fffaf5_0%,_#ffffff_100%)] px-3 py-3 shadow-sm">
                                        <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Career-building note</p>
                                        <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">
                                            {report.synthesis.careerBuilderNote}
                                        </p>
                                        <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#171717] px-2.5 py-1.5 text-[9px] font-bold text-white">
                                            <span>Primary career:</span>
                                            <span>{report.careers.primary.title}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="rounded-[16px] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-3.5 py-3 text-center shadow-sm">
                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#92278F]">Guidance support</p>
                            <p className="mt-1.5 text-[15px] leading-tight font-black text-[#171717] brand-font">Get in Touch with your Yancha Guide for Guidance</p>
                        </div>

                        <p className="pt-0.5 text-center text-[10px] italic text-gray-400 brand-text">{report.footerNote}</p>
                    </div>
                </FixedPrintPageFrame>
            </div>
        </div>
    );
};

export default FullResultsPrintPage;
